import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AppRoutePaths } from 'app/enums/routes.enum';

import { AuthGuard } from 'app/infrastructure/route-guards/auth-guard.service';

import { FuseSharedModule } from '@fuse/shared.module';
import { Error404Component } from './errors/404/error-404.component';
import { Error404Module } from './errors/404/error-404.module';
import { UnloadClientThemeGuard } from './infrastructure/route-guards/unload-client-theme.guard';
import { TenantSelectorComponent } from './pages/tenant-selector/tenant-selector.component';

const appRoutes: Routes = [
    // Use a parent route to hold all client themeable routes
    {
        path: '',
        canActivate: [AuthGuard],    // use a canActivate to load the client's theme
        children: [
            {
                path: AppRoutePaths.HOME,
                loadChildren: () => import('app/pages/home/home.module').then(t => t.HomeModule),
            },
            {
                path: AppRoutePaths.ASSIGN_ROLES,
                loadChildren: () => import('app/pages/manage-roles/manage-roles.module').then(t => t.ManageRolesModule),
            },
            {
              path: AppRoutePaths.ROLE,
              loadChildren: () => import('app/pages/role/role.module').then(t => t.RoleModule),
            },
            {
                path: AppRoutePaths.WORKFLOW,
                loadChildren: () => import('app/pages/workflow/workflow.module').then(m => m.WorkFlowModule),
            },
            {
                path: AppRoutePaths.COMPLAINT,
                loadChildren: () => import('app/pages/complaints/complaints.module').then(m => m.ComplaintsModule),
            },
            {
                path: AppRoutePaths.ALERT,
                loadChildren: () => import('app/pages/alerts/alerts.module').then(m => m.AlertsModule),
            },
            {
                path: AppRoutePaths.PROCEEDINGS,
                loadChildren: () => import('app/pages/proceedings/proceedings.module').then(m => m.ProceedingsModule),
            },
            {
                path: AppRoutePaths.CASES,
                loadChildren: () => import('app/pages/cases/cases.module').then(m => m.CasesModule),
            },
            {
                path: AppRoutePaths.FOLLOWUP_ACTIONS,
                loadChildren: () => import('app/pages/followup-actions/followup-actions.module').then(m => m.FollowUpActionsModule),
            },
            {
                path: AppRoutePaths.PP_FILE_DIRECTORY,
                loadChildren: () => import('app/pages/pp-file-directory/pp-file-directory.module').then(m => m.PPFileDirectoryModule),
            },
            {
                path: AppRoutePaths.PP_VISUALIZATION,
                loadChildren: () => import('app/pages/pp-file-directory/pp-directory-visualization/pp-visualization.module').then(m => m.PPFileVisualizationModule),
            },
            {
                path: AppRoutePaths.PP_APPROVER,
                loadChildren: () => import('app/pages/pp-file-directory/pp-directory-approvers/pp-approver.module').then(m => m.PPFileDirectoryModule),
            },
            {
                path: AppRoutePaths.RISK_MATRIX,
                loadChildren: () => import('app/pages/risk-matrix/risk-matrix.module').then(m => m.RiskMatrixModule),
            },
            {
                path: AppRoutePaths.LABELS,
                loadChildren: () => import('app/pages/labels/labels.module').then(m => m.LabelsModule),
            },
            {
                path: AppRoutePaths.CUSTOM_LABELS,
                loadChildren: () => import('app/pages/custom-labels/custom-labels.module').then(m => m.CustomLabelsModule),
            },
            { 
                path: AppRoutePaths.ERRORNOLANG, 
                loadChildren: () => import('app/errors/error-no-lang/error-no-lang.module').then(m => m.ErrorNoLangModule) 
            },
            {
                path: AppRoutePaths.SECURITY_ROLES,
                loadChildren: () => import('app/pages/security/roles/roles.module').then(t => t.RolesModule),
            },
            {
                path: AppRoutePaths.SECURITY_USERS,
                loadChildren: () => import('app/pages/security/users/users.module').then(t => t.UsersModule),
            },
            {
                path: AppRoutePaths.SECURITY_USERS_GROUPS,
                loadChildren: () => import('app/pages/security/users-groups/users-groups.module').then(t => t.UsersGroupsModule),
            },
            {
                path: AppRoutePaths.TENANTS,
                loadChildren: () => import('app/pages/tenants/tenants.module').then(t => t.TenantsModule),
            },
            {
                path: AppRoutePaths.TENANT_APPROVALS,
                loadChildren: () => import('app/pages/approvals/approvals.module').then(t => t.ApprovalsModule),
            },
            {
                path: AppRoutePaths.SECURITY_AUDIT,
                loadChildren: () => import('app/pages/security/audit/audit.module').then(t => t.AuditModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_FAQS,
                loadChildren: () => import('app/pages/complaints-portal/faqs/faqs.module').then(t => t.FaqsModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_CONTENT_RESOURCES,
                loadChildren: () => import('app/pages/complaints-portal/content-resources/content-resources.module').then(t => t.ContentResourcesModule),
            },            
            {
                path: AppRoutePaths.COMPLAINTS_GENERAL_CONFIGURATION,
                loadChildren: () => import('app/pages/complaints-portal/complaint-settings/complaint-settings.module').then(t => t.ComplaintSettingsModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_CLASSIFICATION,
                loadChildren: () => import('app/pages/complaints-portal/classification/classification.module').then(t => t.ClassificationModule),
            }, 
            {
                path: AppRoutePaths.COMPLAINTS_CRITICALITY,
                loadChildren: () => import('app/pages/complaints-portal/criticality/criticality.module').then(t => t.CriticalityModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_REJECTED_REASON,
                loadChildren: () => import('app/pages/complaints-portal/rejected-reason/rejected-reason.module').then(t => t.RejectedReasonModule),
            },
            {
                path: AppRoutePaths.TYPE_COMPLAINTS,
                loadChildren: () => import('app/pages/complaints-portal/type-complaints/type-complaints.module').then(t => t.TypeComplaintsModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_ALERT_DUE_DATE,
                loadChildren: () => import('app/pages/complaints-portal/alertduedate/alertduedate.module').then(t => t.AlertDueDateModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_AUDIT,
                loadChildren: () => import('app/pages/complaints-portal/audit/audit.module').then(t => t.AuditModule),
            },
            {
                path: AppRoutePaths.COMPLAINTS_ASK_QUESTION,
                loadChildren: () => import('app/pages/complaints-portal/ask-question/ask-question.module').then(t => t.AskQuestionModule),
            },               
            {
                path: AppRoutePaths.WF_MANAGEMENT,
                loadChildren: () => import('app/pages/flow-management/flow-management.module').then(t => t.FlowManagementModule),
            },  
            {
                path: AppRoutePaths.WF_MANAGEMENT_AUDIT,
                loadChildren: () => import('app/pages/flow-management-audit/flow-management-audit.module').then(t => t.FlowManagementAuditModule),
            },   
            {
                path: AppRoutePaths.CONTROL,
                loadChildren: () => import('app/pages/control/controls.module').then(m => m.ControlsModule),
            },
            {
                path: AppRoutePaths.CONTROL_PARAMETERIZATION,
                loadChildren: () => import('app/pages/control-parameterization/control-parameterization.module').then(m => m.ParameterizationsModule),
            },
            {
                path: AppRoutePaths.CONTROL_PARAMETERIZATION_OPOSICION,
                loadChildren: () => import('app/pages/control-parameterization-oposicion/control-parameterization-oposicion.module').then(m => m.ParameterizationsOposicionModule),
            },
            {
                path: AppRoutePaths.CONTROL_PARAMETERIZATION_MANUAL,
                loadChildren: () => import('app/pages/control-parameterization-manual/control-parameterization-manual.module').then(m => m.ParameterizationsManualModule),
            },
            {
                path: AppRoutePaths.CONTROL_PARAMETERIZATION_RULES,
                loadChildren: () => import('app/pages/control-parameterization-rules/control-parameterization-rules.module').then(m => m.ParameterizationsRulesModule),
            },
            {
                path: AppRoutePaths.BOARD,
                loadChildren: () => import('app/pages/boards/boards.module').then(m => m.BoardsModule),
            },
            { 
                path: AppRoutePaths.ERRORNOLANG, 
                loadChildren: () => import('app/errors/error-no-lang/error-no-lang.module').then(m => m.ErrorNoLangModule) 
            },
            {
                path: AppRoutePaths.CONTROL_RESUME,
                loadChildren: () => import('app/pages/control-resume/controls-resume.module').then(m => m.ControlsResumeModule),
            },
            {
                path: AppRoutePaths.ASSOCIATEDPROCESSES,
                loadChildren: () => import('app/pages/associated-processes/associated-processes.module').then(t => t.AssociatedProcessesModule),
            },
            {
                path: AppRoutePaths.PERIODICITY,
                loadChildren: () => import('app/pages/periodicity/periodicity.module').then(t => t.PeriodicityModule),
            },
            {
                path: AppRoutePaths.RESPONSIBLE,
                loadChildren: () => import('app/pages/responsible/responsible.module').then(t => t.ResponsibleModule),
            },
            {
                path: AppRoutePaths.RISK,
                loadChildren: () => import('app/pages/risk/risk.module').then(t => t.RiskModule),
            },
            {
                path: AppRoutePaths.CONTROLTYPE,
                loadChildren: () => import('app/pages/control-type/control-type.module').then(t => t.ControlTypeModule),
            },        
        ]
    },
    // Use parent route to hold all PwC themeable routes, to clean client theme
    {
        path: '',
        canActivate: [UnloadClientThemeGuard],
        children: [
            {
                path: 'tenant-selector',
                component: TenantSelectorComponent,
            },
            {
                path: 'auth-callback',
                loadChildren: () => import('app/auth-callback/auth-callback.module').then(m => m.AuthCallbackModule)
            },
            {
                path: 'logout-callback',
                loadChildren: () => import('app/logout-callback/logout-callback.module').then(m => m.LogoutCallbackModule)
            },
            {
                path: 'login',
                loadChildren: () => import('app/logout-callback/logout-callback.module').then(m => m.LogoutCallbackModule)
            },
        ]
    },
    // {
    //     path: 'workflow',
    //     canActivate: [AuthGuard],
    //     loadChildren: () => import('app/pages/workflow/workflow.module').then(m => m.WorkFlowModule),
    // },
    // {
    //     path: AppRoutePaths.COMPLAINT,
    //     loadChildren: () => import('app/pages/complaints/complaints.module').then(m => m.ComplaintsModule),
    // },
    // {
    //     path: AppRoutePaths.ALERT,
    //     loadChildren: () => import('app/pages/alerts/alerts.module').then(m => m.AlertsModule),
    // },
    // {
    //     path: AppRoutePaths.CASES,
    //     canActivate: [AuthGuard],
    //     loadChildren: () => import('app/pages/cases/cases.module').then(m => m.CasesModule),
    // },
    // {
    //     path: AppRoutePaths.FOLLOWUP_ACTIONS,
    //     loadChildren: () => import('app/pages/followup-actions/followup-actions.module').then(m => m.FollowUpActionsModule),
    // },
    // {
    //     path: AppRoutePaths.LABELS,
    //     canActivate: [AuthGuard],
    //     loadChildren: () => import('app/pages/labels/labels.module').then(m => m.LabelsModule),
    // },
    // {
    //     path: AppRoutePaths.CUSTOM_LABELS,
    //     canActivate: [AuthGuard],
    //     loadChildren: () => import('app/pages/custom-labels/custom-labels.module').then(m => m.CustomLabelsModule),
    // },
    
    { path: AppRoutePaths.FORBIDDEN, loadChildren: () => import('app/errors/403/error-403.module').then(m => m.Error403Module) },
    { path: AppRoutePaths.ERROR, loadChildren: () => import('app/errors/500/error-500.module').then(m => m.Error500Module) },

    // { path: AppRoutePaths.ERRORNOLANG, 
    //     canActivate: [AuthGuard], 
    //     loadChildren: () => import('app/errors/error-no-lang/error-no-lang.module').then(m => m.ErrorNoLangModule) 
    // },
    // { path: '**', loadChildren: () => import('app/errors/404/error-404.module').then(m => m.Error404Module)}
    {
        path: '**',
        component: Error404Component,
    },

];

@NgModule({
    declarations: [
    ],
    imports: [
        RouterModule.forRoot(appRoutes, {
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled',
            enableTracing: false,
            onSameUrlNavigation: 'reload'
        }),
        Error404Module,
        FuseSharedModule,
    ],
    exports: [
        RouterModule,
    ]
})
export class AppRoutingModule { }

